import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

const MAX_WORLD_SIZE = 600;
const TARGET_RADIUS = 38;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const shiftOrbitPivot = (camera, controls, pivot) => {
  if (!camera || !controls || !pivot) return;
  camera.position.add(pivot.clone().sub(controls.target));
  controls.target.copy(pivot);
  controls.update();
};

const disposeObject = (object) => {
  object?.traverse((child) => {
    child.geometry?.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.filter(Boolean).forEach((material) => {
      Object.values(material).forEach((value) => value?.isTexture && value.dispose());
      material.dispose();
    });
  });
};

const loadMapImage = (mapImage) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = mapImage.startsWith('data:') ? mapImage : `data:image/png;base64,${mapImage}`;
});

const createLabelSprite = (text, background = '#0b4f71', accent = '#38bdf8') => {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 160;
  const context = canvas.getContext('2d');
  context.shadowColor = 'rgba(0, 0, 0, 0.45)';
  context.shadowBlur = 18;
  context.shadowOffsetY = 8;
  context.fillStyle = background;
  context.beginPath();
  context.roundRect(18, 18, 604, 116, 28);
  context.fill();
  context.shadowColor = 'transparent';
  context.strokeStyle = accent;
  context.lineWidth = 5;
  context.stroke();
  context.fillStyle = '#f8fafc';
  context.font = '700 52px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(String(text), 320, 77, 560);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  }));
  sprite.scale.set(42, 10.5, 1);
  sprite.renderOrder = 24;
  return sprite;
};

const createRobotMarker = (name) => {
  const group = new THREE.Group();
  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(9, 40),
    new THREE.MeshBasicMaterial({ color: 0x020617, transparent: true, opacity: 0.42, depthWrite: false })
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.08;
  group.add(shadow);

  const halo = new THREE.Mesh(
    new THREE.TorusGeometry(10, 1.15, 12, 56),
    new THREE.MeshBasicMaterial({ color: 0xfb7185, transparent: true, opacity: 0.75, depthTest: false })
  );
  halo.rotation.x = Math.PI / 2;
  halo.position.y = 0.28;
  halo.renderOrder = 30;
  group.add(halo);

  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(6.4, 7.2, 4.2, 32),
    new THREE.MeshStandardMaterial({ color: 0xe11d48, roughness: 0.38, metalness: 0.1 })
  );
  body.position.y = 2.45;
  body.castShadow = true;
  group.add(body);

  const border = new THREE.Mesh(
    new THREE.TorusGeometry(6.5, 0.65, 10, 40),
    new THREE.MeshBasicMaterial({ color: 0xfff1f2, depthTest: false })
  );
  border.rotation.x = Math.PI / 2;
  border.position.y = 4.65;
  border.renderOrder = 31;
  group.add(border);

  const arrow = new THREE.Mesh(
    new THREE.ConeGeometry(2.7, 9, 16),
    new THREE.MeshBasicMaterial({ color: 0xfbbf24, depthTest: false })
  );
  arrow.rotation.z = -Math.PI / 2;
  arrow.position.set(7.7, 4.75, 0);
  arrow.renderOrder = 32;
  group.add(arrow);

  const label = createLabelSprite(name || 'Robot', '#881337', '#fb7185');
  label.position.set(0, 14.5, 0);
  group.add(label);
  group.userData.halo = halo;
  return group;
};

const createWaypointMarker = (waypoint, selected) => {
  const group = new THREE.Group();
  const footprint = new THREE.Mesh(
    new THREE.CircleGeometry(5.8, 32),
    new THREE.MeshBasicMaterial({ color: 0x082f49, transparent: true, opacity: 0.76, depthWrite: false })
  );
  footprint.rotation.x = -Math.PI / 2;
  footprint.position.y = 0.08;
  footprint.userData.waypointGroup = group;
  footprint.renderOrder = 12;
  group.add(footprint);

  const baseRing = new THREE.Mesh(
    new THREE.TorusGeometry(5.7, 0.62, 10, 40),
    new THREE.MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.95, depthTest: false })
  );
  baseRing.rotation.x = Math.PI / 2;
  baseRing.position.y = 0.22;
  baseRing.userData.waypointGroup = group;
  baseRing.renderOrder = 16;
  group.add(baseRing);

  const stem = new THREE.Mesh(
    new THREE.CylinderGeometry(0.48, 0.7, 2.8, 14),
    new THREE.MeshStandardMaterial({ color: 0x7dd3fc, roughness: 0.36, metalness: 0.18 })
  );
  stem.position.y = 1.55;
  stem.userData.waypointGroup = group;
  group.add(stem);

  const marker = new THREE.Mesh(
    new THREE.SphereGeometry(2.15, 22, 16),
    new THREE.MeshStandardMaterial({
      color: selected ? 0xf59e0b : 0x0284c7,
      emissive: selected ? 0x7c2d12 : 0x082f49,
      emissiveIntensity: 0.55,
      roughness: 0.28,
    })
  );
  marker.position.y = 3.35;
  marker.castShadow = true;
  marker.userData.waypointGroup = group;
  marker.renderOrder = 18;
  group.add(marker);

  const pulse = new THREE.Mesh(
    new THREE.TorusGeometry(7.1, 0.82, 10, 48),
    new THREE.MeshBasicMaterial({ color: 0xfbbf24, transparent: true, opacity: 0.82, depthTest: false })
  );
  pulse.rotation.x = Math.PI / 2;
  pulse.position.y = 0.25;
  pulse.visible = selected;
  pulse.renderOrder = 15;
  pulse.userData.waypointGroup = group;
  group.add(pulse);

  const label = createLabelSprite(waypoint.WaypointName || 'Waypoint');
  label.position.y = 10.5;
  group.add(label);
  group.userData = { waypoint, marker, pulse, label, baseRing };
  group.children.forEach((child) => { child.userData.waypointGroup = group; });
  return group;
};

const createPiecewiseRouteCurve = (positions) => {
  const curve = new THREE.CurvePath();
  for (let index = 0; index < positions.length - 1; index += 1) {
    curve.add(new THREE.LineCurve3(positions[index], positions[index + 1]));
  }
  return curve;
};
export default function Map3D({
  robot,
  camerasVisible,
  mapImage,
  mapName = '',
  waypoints = [],
  selectedWaypointName = '',
  robotLocation = null,
  onWaypointClick,
  onPointTargetClick,
  onPointTargetChange,
  routePoints = [],
  routeState = 'idle',
  targetLocked = false,
  missionStatus = '',
  missionTarget = null,
}) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const missionLockLabel = missionStatus === 'PENDING'
    ? 'Mission scheduled'
    : missionStatus === 'PAUSED'
      ? 'Mission paused'
      : missionStatus === 'IN_PROGRESS'
        ? 'Robot en route'
        : 'Navigation locked';
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const groundRef = useRef(null);
  const robotGroupRef = useRef(null);
  const waypointsRef = useRef([]);
  const targetGroupRef = useRef(null);
  const routeGroupRef = useRef(null);
  const targetRef = useRef(null);
  const hadMissionTargetRef = useRef(false);
  const metricsRef = useRef(null);
  const animationRef = useRef(null);
  const hoveredRef = useRef(null);
  const pointerDownRef = useRef(null);
  const targetDragRef = useRef(null);
  const pointTargetModeRef = useRef(false);
  const targetLockedRef = useRef(false);
  const orbitPivotRef = useRef('robot');
  const callbacksRef = useRef({ onWaypointClick, onPointTargetClick, onPointTargetChange });
  const displayRef = useRef({ selectedWaypointName, showWaypointNames: false });
  const [sceneVersion, setSceneVersion] = useState(0);
  const [sceneError, setSceneError] = useState('');
  const [showRobot, setShowRobot] = useState(true);
  const [showWaypoints, setShowWaypoints] = useState(true);
  const [showWaypointNames, setShowWaypointNames] = useState(false);
  const [waypointSearch, setWaypointSearch] = useState('');
  const [pointTargetMode, setPointTargetMode] = useState(false);
  const [temporaryTarget, setTemporaryTarget] = useState(null);
  const [orbitPivot, setOrbitPivot] = useState('robot');

  callbacksRef.current = { onWaypointClick, onPointTargetClick, onPointTargetChange };
  displayRef.current = { selectedWaypointName, showWaypointNames };
  pointTargetModeRef.current = pointTargetMode;
  targetLockedRef.current = targetLocked;
  orbitPivotRef.current = orbitPivot;
  const updateWaypointVisuals = useCallback(() => {
    const selectedName = displayRef.current.selectedWaypointName;
    const namesVisible = displayRef.current.showWaypointNames;
    waypointsRef.current.forEach((group) => {
      const { waypoint, marker, pulse, label, baseRing } = group.userData;
      const selected = waypoint.WaypointName === selectedName;
      const hovered = group === hoveredRef.current;
      marker.material.color.set(selected ? 0xf59e0b : hovered ? 0x38bdf8 : 0x0284c7);
      marker.material.emissive.set(selected ? 0x7c2d12 : hovered ? 0x075985 : 0x082f49);
      baseRing.material.color.set(selected ? 0xfbbf24 : hovered ? 0x7dd3fc : 0x38bdf8);
      pulse.visible = selected;
      label.visible = namesVisible || hovered || selected;
    });
  }, []);

  const syncTargetGroup = useCallback((target) => {
    const group = targetGroupRef.current;
    const metrics = metricsRef.current;
    if (!group || !metrics || !target) return;
    const position = metrics.pixelToWorld(target.x, target.y);
    group.position.set(position.x, 0.12, position.z);
    group.rotation.y = target.angle || 0;
  }, []);

  const setTarget = useCallback((target) => {
    targetRef.current = target;
    setTemporaryTarget(target);
    syncTargetGroup(target);
    callbacksRef.current.onPointTargetChange?.(target);
  }, [syncTargetGroup]);

  const clearTarget = useCallback(() => {
    targetRef.current = null;
    setTemporaryTarget(null);
    callbacksRef.current.onPointTargetChange?.(null);
    if (targetGroupRef.current && sceneRef.current) {
      sceneRef.current.remove(targetGroupRef.current);
      disposeObject(targetGroupRef.current);
      targetGroupRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (missionTarget) {
      const target = {
        x: Number(missionTarget.x),
        y: Number(missionTarget.y),
        z: Number(missionTarget.z || 0),
        angle: Number(missionTarget.heading ?? missionTarget.angle ?? 0),
      };
      if (!Number.isFinite(target.x) || !Number.isFinite(target.y)) return;
      hadMissionTargetRef.current = true;
      targetRef.current = target;
      setTemporaryTarget(target);
      setPointTargetMode(true);
      syncTargetGroup(target);
      return;
    }

    if (hadMissionTargetRef.current) {
      hadMissionTargetRef.current = false;
      targetRef.current = null;
      setTemporaryTarget(null);
      setPointTargetMode(false);
      if (targetGroupRef.current && sceneRef.current) {
        sceneRef.current.remove(targetGroupRef.current);
        disposeObject(targetGroupRef.current);
        targetGroupRef.current = null;
      }
    }
  }, [missionTarget, syncTargetGroup]);

  const resetView = useCallback(() => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const metrics = metricsRef.current;
    if (!camera || !controls || !metrics) return;
    const pivot = orbitPivotRef.current === 'robot'
      ? robotGroupRef.current?.position || new THREE.Vector3()
      : new THREE.Vector3(0, 0.12, 0);
    const distance = Math.max(metrics.worldWidth, metrics.worldDepth) * 0.78;
    camera.position.set(
      pivot.x + distance * 0.55,
      pivot.y + distance * 0.68,
      pivot.z + distance * 0.55
    );
    controls.target.copy(pivot);
    controls.update();
  }, []);

  useEffect(() => {
    if (!containerRef.current || !mapImage) return undefined;
    let cancelled = false;
    let resizeObserver;
    let handleResize;
    const container = containerRef.current;

    const initialise = async () => {
      try {
        setSceneError('');
        const image = await loadMapImage(mapImage);
        if (cancelled) return;

        const unitsPerPixel = MAX_WORLD_SIZE / Math.max(image.naturalWidth, image.naturalHeight);
        const metrics = {
          imageWidth: image.naturalWidth,
          imageHeight: image.naturalHeight,
          unitsPerPixel,
          worldWidth: image.naturalWidth * unitsPerPixel,
          worldDepth: image.naturalHeight * unitsPerPixel,
          pixelToWorld: (pixelX, pixelY, pixelZ = 0) => ({
            x: (Number(pixelX) - image.naturalWidth / 2) * unitsPerPixel,
            y: 1.1 + Math.max(0, Number(pixelZ) || 0) * unitsPerPixel,
            z: (image.naturalHeight / 2 - Number(pixelY)) * unitsPerPixel,
          }),
          worldToPixel: (worldX, worldZ) => ({
            x: clamp(worldX / unitsPerPixel + image.naturalWidth / 2, 0, image.naturalWidth),
            y: clamp(image.naturalHeight / 2 - worldZ / unitsPerPixel, 0, image.naturalHeight),
          }),
        };
        metricsRef.current = metrics;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x07131d);
        scene.fog = new THREE.Fog(0x07131d, 620, 1200);
        sceneRef.current = scene;

        const width = Math.max(1, container.clientWidth);
        const height = Math.max(1, container.clientHeight);
        const camera = new THREE.PerspectiveCamera(52, width / height, 0.1, 2000);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.domElement.style.display = 'block';
        renderer.domElement.style.width = '100%';
        renderer.domElement.style.height = '100%';
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(width, height, false);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        container.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.enablePan = false;
        controls.minDistance = 35;
        controls.maxDistance = 1000;
        controls.maxPolarAngle = Math.PI / 2.02;
        controls.mouseButtons = {
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.ROTATE,
        };
        controlsRef.current = controls;

        scene.add(new THREE.HemisphereLight(0xffffff, 0x263238, 1.6));
        const keyLight = new THREE.DirectionalLight(0xffffff, 1.7);
        keyLight.position.set(180, 280, 120);
        keyLight.castShadow = true;
        keyLight.shadow.mapSize.set(1024, 1024);
        scene.add(keyLight);

        const texture = new THREE.Texture(image);
        texture.needsUpdate = true;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        const ground = new THREE.Mesh(
          new THREE.PlaneGeometry(metrics.worldWidth, metrics.worldDepth),
          new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.receiveShadow = true;
        ground.userData.isGround = true;
        scene.add(ground);
        groundRef.current = ground;

        const robotGroup = createRobotMarker(robot?.RobotName || robot?.RobotId || 'Robot');
        scene.add(robotGroup);
        robotGroupRef.current = robotGroup;
        resetView();

        const raycaster = new THREE.Raycaster();
        const pointer = new THREE.Vector2();
        const getHits = (event, objects, recursive = true) => {
          const bounds = renderer.domElement.getBoundingClientRect();
          pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
          pointer.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
          raycaster.setFromCamera(pointer, camera);
          return raycaster.intersectObjects(objects.filter(Boolean), recursive);
        };
        const getGroundPoint = (event) => getHits(event, [ground], false)[0]?.point;
        const onPointerMove = (event) => {
          if (targetDragRef.current && targetRef.current) {
            const worldPoint = getGroundPoint(event);
            if (!worldPoint) return;
            if (targetDragRef.current === 'move') {
              const pixels = metrics.worldToPixel(worldPoint.x, worldPoint.z);
              setTarget({ ...targetRef.current, x: pixels.x, y: pixels.y });
            } else {
              const targetWorld = metrics.pixelToWorld(targetRef.current.x, targetRef.current.y);
              const angle = Math.atan2(-(worldPoint.z - targetWorld.z), worldPoint.x - targetWorld.x);
              setTarget({ ...targetRef.current, angle });
            }
            return;
          }

          const hit = getHits(event, waypointsRef.current)[0];
          const group = hit?.object?.userData?.waypointGroup || null;
          if (group !== hoveredRef.current) {
            hoveredRef.current = group;
            renderer.domElement.style.cursor = group ? 'pointer' : pointTargetModeRef.current ? 'crosshair' : 'grab';
            updateWaypointVisuals();
          }
        };

        const onPointerDown = (event) => {
          if (event.button !== 0) return;
          pointerDownRef.current = { x: event.clientX, y: event.clientY };
          if (!pointTargetModeRef.current || targetLockedRef.current || !targetGroupRef.current) return;
          const targetHit = getHits(event, [targetGroupRef.current])[0]?.object;
          if (targetHit?.userData.targetPart === 'center') {
            targetDragRef.current = 'move';
            controls.enabled = false;
          } else if (targetHit?.userData.targetPart === 'heading') {
            targetDragRef.current = 'heading';
            controls.enabled = false;
          }
        };

        const onPointerUp = (event) => {
          const start = pointerDownRef.current;
          const moved = start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5;
          const dragMode = targetDragRef.current;
          pointerDownRef.current = null;
          targetDragRef.current = null;
          controls.enabled = true;
          if (moved || event.button !== 0) return;

          const waypointHit = getHits(event, waypointsRef.current)[0];
          const waypointGroup = waypointHit?.object?.userData?.waypointGroup;
          if (waypointGroup) {
            callbacksRef.current.onWaypointClick?.(waypointGroup.userData.waypoint);
            return;
          }
          if (!pointTargetModeRef.current || targetLockedRef.current) return;

          if (dragMode === 'heading') return;
          if (dragMode === 'move' && targetRef.current) {
            callbacksRef.current.onPointTargetClick?.(targetRef.current, clearTarget);
            return;
          }
          const targetHit = targetGroupRef.current && getHits(event, [targetGroupRef.current])[0]?.object;
          if (targetHit?.userData.targetPart === 'center' && targetRef.current) {
            callbacksRef.current.onPointTargetClick?.(targetRef.current, clearTarget);
            return;
          }
          const worldPoint = getGroundPoint(event);
          if (worldPoint) {
            const pixels = metrics.worldToPixel(worldPoint.x, worldPoint.z);
            setTarget({ x: pixels.x, y: pixels.y, z: 0, angle: targetRef.current?.angle || 0 });
          }
        };

        const onPointerLeave = () => {
          if (targetDragRef.current) {
            targetDragRef.current = null;
            controls.enabled = true;
          }
          hoveredRef.current = null;
          updateWaypointVisuals();
        };

        renderer.domElement.addEventListener('pointermove', onPointerMove);
        renderer.domElement.addEventListener('pointerdown', onPointerDown);
        renderer.domElement.addEventListener('pointerup', onPointerUp);
        renderer.domElement.addEventListener('pointerleave', onPointerLeave);

        handleResize = () => {
          const nextWidth = Math.max(1, container.clientWidth);
          const nextHeight = Math.max(1, container.clientHeight);
          const nextPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
          if (renderer.getPixelRatio() !== nextPixelRatio) {
            renderer.setPixelRatio(nextPixelRatio);
          }
          camera.aspect = nextWidth / nextHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(nextWidth, nextHeight, false);
        };
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(container);
        window.addEventListener('resize', handleResize);

        let lastFrame = 0;
        const animate = (now) => {
          animationRef.current = requestAnimationFrame(animate);
          if (now - lastFrame < 1000 / 45) return;
          lastFrame = now;
          controls.update();
          const cameraDistance = camera.position.distanceTo(controls.target);
          const markerScale = clamp(cameraDistance / 700, 0.14, 0.65);
          const pulseScale = 1 + Math.sin(now * 0.006) * 0.22;
          waypointsRef.current.forEach((group) => {
            group.scale.setScalar(markerScale);
            if (group.userData.pulse.visible) group.userData.pulse.scale.setScalar(pulseScale);
          });
          if (routeGroupRef.current?.userData.curve) {
            const routePulse = (Math.sin(now * 0.0045) + 1) / 2;
            const routeData = routeGroupRef.current.userData;
            routeData.ribbonMaterial.opacity = routeData.animated
              ? 0.76 + routePulse * 0.2
              : 0.72;
            routeData.ribbonMaterial.emissiveIntensity = routeData.animated
              ? 0.55 + routePulse * 0.45
              : 0.45;
            routeData.runners.forEach((runner, index) => {
              const progress = (now * 0.000055 + index / routeData.runners.length) % 1;
              runner.position.copy(routeData.curve.getPointAt(progress));
              const runnerPulse = 0.78 + routePulse * 0.34;
              runner.scale.setScalar(runnerPulse);
            });
          }
          if (targetGroupRef.current) {
            targetGroupRef.current.scale.setScalar(markerScale);
            targetGroupRef.current.userData.pulse?.scale.setScalar(pulseScale);
          }
          if (robotGroupRef.current) {
            robotGroupRef.current.scale.setScalar(markerScale * 1.08);
            robotGroupRef.current.userData.halo?.scale.setScalar(1 + Math.sin(now * 0.004) * 0.1);
          }
          renderer.render(scene, camera);
        };
        animate(0);
        setSceneVersion((version) => version + 1);

        scene.userData.cleanupInteractions = () => {
          renderer.domElement.removeEventListener('pointermove', onPointerMove);
          renderer.domElement.removeEventListener('pointerdown', onPointerDown);
          renderer.domElement.removeEventListener('pointerup', onPointerUp);
          renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
        };
      } catch (error) {
        console.error('Failed to initialise 2.5D map:', error);
        if (!cancelled) setSceneError('Unable to render this map in 2.5D.');
      }
    };

    initialise();
    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (handleResize) window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      sceneRef.current?.userData.cleanupInteractions?.();
      controlsRef.current?.dispose();
      if (sceneRef.current) disposeObject(sceneRef.current);
      const renderer = rendererRef.current;
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
        if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
      }
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      controlsRef.current = null;
      groundRef.current = null;
      robotGroupRef.current = null;
      waypointsRef.current = [];
      targetGroupRef.current = null;
      routeGroupRef.current = null;
      metricsRef.current = null;
    };
  }, [robot, mapImage, clearTarget, resetView, setTarget, updateWaypointVisuals]);
  useEffect(() => {
    const scene = sceneRef.current;
    const metrics = metricsRef.current;
    if (!scene || !metrics) return;
    waypointsRef.current.forEach((group) => {
      scene.remove(group);
      disposeObject(group);
    });
    waypointsRef.current = [];
    if (!showWaypoints) return;

    const query = waypointSearch.trim().toLowerCase();
    waypoints
      .filter((waypoint) => !query || String(waypoint.WaypointName || '').toLowerCase().includes(query))
      .forEach((waypoint) => {
        const group = createWaypointMarker(waypoint, false);
        const position = metrics.pixelToWorld(waypoint.X, waypoint.Y, waypoint.Z);
        group.position.set(position.x, 0.12, position.z);
        scene.add(group);
        waypointsRef.current.push(group);
      });
    updateWaypointVisuals();
  }, [sceneVersion, waypoints, waypointSearch, showWaypoints, updateWaypointVisuals]);

  useEffect(() => {
    updateWaypointVisuals();
  }, [selectedWaypointName, showWaypointNames, updateWaypointVisuals]);

  useEffect(() => {
    if (robotGroupRef.current) robotGroupRef.current.visible = showRobot;
  }, [showRobot, sceneVersion]);

  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (canvas) canvas.style.cursor = hoveredRef.current ? 'pointer' : pointTargetMode ? 'crosshair' : 'grab';
  }, [pointTargetMode, sceneVersion]);

  useEffect(() => {
    const group = robotGroupRef.current;
    const metrics = metricsRef.current;
    if (!group || !metrics || !robotLocation) return;
    const position = metrics.pixelToWorld(robotLocation.x, robotLocation.y, robotLocation.z);
    const nextPivot = new THREE.Vector3(position.x, 0.12, position.z);
    group.position.copy(nextPivot);
    group.rotation.y = Number(robotLocation.heading) || 0;

    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (orbitPivotRef.current === 'robot') shiftOrbitPivot(camera, controls, nextPivot);
  }, [robotLocation, sceneVersion]);

  useEffect(() => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;
    controls.enablePan = orbitPivot === 'map';
    controls.mouseButtons.LEFT = orbitPivot === 'map'
      ? THREE.MOUSE.PAN
      : THREE.MOUSE.ROTATE;
    const pivot = orbitPivot === 'robot'
      ? robotGroupRef.current?.position || new THREE.Vector3()
      : new THREE.Vector3(0, 0.12, 0);
    shiftOrbitPivot(camera, controls, pivot);
  }, [orbitPivot, sceneVersion]);

  useEffect(() => {
    const scene = sceneRef.current;
    const metrics = metricsRef.current;
    if (!scene || !metrics) return;

    if (routeGroupRef.current) {
      scene.remove(routeGroupRef.current);
      disposeObject(routeGroupRef.current);
      routeGroupRef.current = null;
    }
    if (routePoints.length < 2) return;

    const positions = routePoints
      .map((point) => {
        const position = metrics.pixelToWorld(point.x, point.y);
        return new THREE.Vector3(position.x, 0.62, position.z);
      })
      .filter((position, index, all) => (
        index === 0 || position.distanceTo(all[index - 1]) > 0.02
      ));
    if (positions.length < 2) return;

    const palette = routeState === 'error'
      ? { ribbon: 0xef4444, emissive: 0x7f1d1d, runner: 0xfecaca }
      : routeState === 'paused'
        ? { ribbon: 0xf59e0b, emissive: 0x78350f, runner: 0xfef3c7 }
        : routeState === 'travelling'
          ? { ribbon: 0x22c55e, emissive: 0x14532d, runner: 0xbbf7d0 }
          : routeState === 'scheduled'
            ? { ribbon: 0x38bdf8, emissive: 0x0c4a6e, runner: 0xbae6fd }
            : { ribbon: 0x22d3ee, emissive: 0x155e75, runner: 0xcffafe };
    const curve = createPiecewiseRouteCurve(positions);
    const routeLength = curve.getLength();
    const tubularSegments = Math.round(clamp(routeLength / 1.8, 28, 520));
    const group = new THREE.Group();

    const underlayMaterial = new THREE.MeshBasicMaterial({
      color: 0x020617,
      transparent: true,
      opacity: 0.58,
      depthWrite: false,
    });
    const underlay = new THREE.Mesh(
      new THREE.TubeGeometry(curve, tubularSegments, 1.5, 8, false),
      underlayMaterial
    );
    underlay.position.y = -0.12;
    underlay.renderOrder = 7;
    group.add(underlay);

    const ribbonMaterial = new THREE.MeshStandardMaterial({
      color: palette.ribbon,
      emissive: palette.emissive,
      emissiveIntensity: 0.8,
      roughness: 0.32,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const ribbon = new THREE.Mesh(
      new THREE.TubeGeometry(curve, tubularSegments, 0.72, 10, false),
      ribbonMaterial
    );
    ribbon.renderOrder = 8;
    group.add(ribbon);

    const runnerCount = routeState === 'scheduled' ? 0 : 5;
    const runners = Array.from({ length: runnerCount }, (_, index) => {
      const runner = new THREE.Mesh(
        new THREE.SphereGeometry(1.45, 14, 10),
        new THREE.MeshBasicMaterial({
          color: palette.runner,
          transparent: true,
          opacity: 0.98,
          depthTest: false,
        })
      );
      runner.position.copy(curve.getPointAt(index / runnerCount));
      runner.renderOrder = 10;
      group.add(runner);
      return runner;
    });

    const startCap = new THREE.Mesh(
      new THREE.CylinderGeometry(2.2, 2.2, 0.5, 24),
      new THREE.MeshBasicMaterial({ color: palette.ribbon, transparent: true, opacity: 0.86 })
    );
    startCap.position.copy(positions[0]);
    startCap.position.y = 0.42;
    group.add(startCap);

    const endCap = startCap.clone();
    endCap.position.copy(positions[positions.length - 1]);
    endCap.position.y = 0.42;
    group.add(endCap);

    group.userData = {
      curve,
      runners,
      ribbonMaterial,
      underlayMaterial,
      animated: routeState !== 'scheduled',
    };
    scene.add(group);
    routeGroupRef.current = group;
  }, [routePoints, routeState, sceneVersion]);
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !metricsRef.current) return;
    if (!temporaryTarget) {
      if (targetGroupRef.current) {
        scene.remove(targetGroupRef.current);
        disposeObject(targetGroupRef.current);
        targetGroupRef.current = null;
      }
      return;
    }
    if (!targetGroupRef.current) {
      const group = new THREE.Group();
      const pulse = new THREE.Mesh(
        new THREE.TorusGeometry(10, 1.4, 12, 48),
        new THREE.MeshBasicMaterial({ color: 0xffb300, transparent: true, opacity: 0.9, depthTest: false })
      );
      pulse.rotation.x = Math.PI / 2;
      pulse.userData.targetPart = 'center';
      pulse.renderOrder = 40;
      group.add(pulse);

      const center = new THREE.Mesh(
        new THREE.CylinderGeometry(5, 5, 3, 24),
        new THREE.MeshBasicMaterial({ color: 0xff6f00, depthTest: false })
      );
      center.position.y = 2;
      center.userData.targetPart = 'center';
      center.renderOrder = 41;
      group.add(center);

      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(TARGET_RADIUS, 1.2, 10, 64),
        new THREE.MeshBasicMaterial({ color: 0xffca28, transparent: true, opacity: 0.8, depthTest: false })
      );
      ring.rotation.x = Math.PI / 2;
      ring.userData.targetPart = 'heading';
      ring.renderOrder = 39;
      group.add(ring);

      const direction = new THREE.Mesh(
        new THREE.BoxGeometry(TARGET_RADIUS, 1.4, 1.4),
        new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false })
      );
      direction.position.set(TARGET_RADIUS / 2, 1.5, 0);
      direction.userData.targetPart = 'heading';
      direction.renderOrder = 42;
      group.add(direction);

      const handle = new THREE.Mesh(
        new THREE.SphereGeometry(4, 18, 12),
        new THREE.MeshBasicMaterial({ color: 0xffb300, depthTest: false })
      );
      handle.position.set(TARGET_RADIUS, 1.5, 0);
      handle.userData.targetPart = 'heading';
      handle.renderOrder = 43;
      group.add(handle);
      group.userData.pulse = pulse;
      scene.add(group);
      targetGroupRef.current = group;
    }
    syncTargetGroup(temporaryTarget);
  }, [temporaryTarget, sceneVersion, syncTargetGroup]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 420,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '6px',
        backgroundColor: '#101820',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          zIndex: 50,
          top: camerasVisible ? 310 : 48,
          left: 10,
          right: 10,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderRadius: 1,
          backgroundColor: 'rgba(10, 20, 28, 0.88)',
          color: '#fff',
          transition: 'top 300ms ease-in-out',
        }}
      >
        <Typography variant="subtitle2" sx={{ mr: 1, maxWidth: 180 }} noWrap>
          {mapName || '2.5D Map'}
        </Typography>
        <Button
          size="small"
          variant={pointTargetMode ? 'contained' : 'outlined'}
          color={pointTargetMode ? 'warning' : 'inherit'}
          startIcon={<AddLocationAltIcon />}
          onClick={() => setPointTargetMode((enabled) => !enabled)}
          disabled={targetLocked}
        >
          {targetLocked ? missionLockLabel : 'Send to point'}
        </Button>
        <Button size="small" color="inherit" startIcon={<CenterFocusStrongIcon />} onClick={resetView}>
          Reset view
        </Button>
        <FormControlLabel
          label="Fixed on robot"
          sx={{
            height: 32,
            m: 0,
            px: 0.75,
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 1,
            '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
          }}
          control={(
            <Switch
              size="small"
              checked={orbitPivot === 'robot'}
              onChange={(event) => setOrbitPivot(event.target.checked ? 'robot' : 'map')}
              inputProps={{ 'aria-label': 'Keep camera fixed on robot' }}
            />
          )}
        />
        {temporaryTarget && !targetLocked && <Button size="small" color="warning" onClick={clearTarget}>Clear point</Button>}
        <TextField
          size="small"
          value={waypointSearch}
          onChange={(event) => setWaypointSearch(event.target.value)}
          placeholder="Filter waypoints"
          sx={{ width: 150, '& .MuiInputBase-root': { color: '#fff', height: 32 } }}
        />
        <FormControlLabel control={<Switch size="small" checked={showRobot} onChange={(event) => setShowRobot(event.target.checked)} />} label="Robot" />
        <FormControlLabel control={<Switch size="small" checked={showWaypoints} onChange={(event) => setShowWaypoints(event.target.checked)} />} label="Waypoints" />
        <FormControlLabel control={<Switch size="small" checked={showWaypointNames} onChange={(event) => setShowWaypointNames(event.target.checked)} />} label="Names" />
      </Box>

      <Box sx={{ position: 'absolute', zIndex: 45, left: 12, bottom: 10, px: 1, py: 0.5, borderRadius: 1, color: '#fff', backgroundColor: 'rgba(10,20,28,0.78)', pointerEvents: 'none' }}>
        <Typography variant="caption">
          {orbitPivot === 'robot'
            ? 'Drag: orbit around robot · Wheel: zoom'
            : 'Left drag: pan · Right drag: orbit · Wheel: zoom'}
          {pointTargetMode ? ' · Click map: place · Drag point: move · Drag ring: heading · Click point: confirm' : ''}
        </Typography>
      </Box>

      {temporaryTarget && (
        <Box sx={{ position: 'absolute', zIndex: 45, right: 12, bottom: 10, px: 1, py: 0.5, borderRadius: 1, color: '#fff', backgroundColor: 'rgba(10,20,28,0.78)', pointerEvents: 'none' }}>
          <Typography variant="caption">
            X {temporaryTarget.x.toFixed(1)} · Y {temporaryTarget.y.toFixed(1)} · Heading {(temporaryTarget.angle * 180 / Math.PI).toFixed(0)}°
          </Typography>
        </Box>
      )}

      {sceneError && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 60, display: 'grid', placeItems: 'center', color: '#fff', backgroundColor: 'rgba(0,0,0,0.65)' }}>
          <Typography>{sceneError}</Typography>
        </Box>
      )}
    </Box>
  );
}